import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { ClassifierData } from 'types/classifiers';

import { LockIcon } from 'components/icons';
import SharedPaymentTotalsCard from 'pages/invoices/payers/SharedPaymentTotalsCard';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback } from 'react';
import { useCreateInvoiceRowMutation, useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData, InvoiceRowForm, InvoiceTotalsForm, InvoiceType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import {
    mapInvoiceRowFormToAdminFeeCreateDto,
    mapInvoiceRowFormToInvoiceRowCreateDto,
    mapInvoiceTotalsToInvoiceTotalsFormDto,
} from '../../../util/InvoicesPageUtil';
import RecalculatingOverlay from '../../components/RecalculatingOverlay';
import { SECTION_CARD, SECTION_HEADER, SECTION_TITLE } from '../details-tab/SectionCard';
import InvoiceMatterTotals from './InvoiceMatterTotals';
import InvoiceTotalsCard from './InvoiceTotalsCard';
import RoundingMatrixAccordion from './RoundingMatrixAccordion';

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
    classifiers: Record<string, ClassifierData>;
    isLoading: boolean;
}

const Totals = ({ invoice, classifiers, isLoading, isInViewMode }: Props) => {
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const hasRoundingMatrixPermission = useHasPermissions(['UPDATE.INVOICES.ROUNDING_MATRIX']);
    const formMethods = useForm<Partial<InvoiceTotalsForm>>({
        values: mapInvoiceTotalsToInvoiceTotalsFormDto(invoiceTotals),
        defaultValues: mapInvoiceTotalsToInvoiceTotalsFormDto(invoiceTotals),
    });
    const isCorrectionInvocie = invoice.invoiceTypeCode === InvoiceType.CORRECTION;
    const titleId = isCorrectionInvocie ? 'invoices.totals-tab.partial-credit-note-title' : 'invoices.totals-tab.title';
    const isTotalsReadOnly = invoice.invoiceTypeCode === InvoiceType.CREDIT;
    const { isSharedPaymentCopy, hasPaymentShare } = useSharedPayment(invoice);
    // A credit note of a copy carries the copy's share and full amounts, negated, without being a copy itself.
    const hasShareTotals = isSharedPaymentCopy || (hasPaymentShare && !!invoice.preSplitTotals);

    const [createInvoiceRow] = useCreateInvoiceRowMutation();

    const handleAddRow = useCallback(
        async (data: InvoiceRowForm) => {
            const invoiceRowCreateDto = mapInvoiceRowFormToInvoiceRowCreateDto(data, invoice.id);
            await createInvoiceRow(invoiceRowCreateDto);
        },
        [createInvoiceRow, invoice]
    );

    const handleAddAdminFee = useCallback(
        async (data: InvoiceRowForm) => {
            const dto = mapInvoiceRowFormToAdminFeeCreateDto(data, invoice.id);
            await createInvoiceRow(dto);
        },
        [createInvoiceRow, invoice]
    );

    return (
        <FormProvider {...formMethods}>
            <div className="new:gap-(--card-gap) relative flex flex-col gap-6">
                {isSharedPaymentCopy ? (
                    <div className={cn(SECTION_CARD, 'flex items-start gap-2 px-6 py-4')}>
                        <LockIcon
                            className="text-fg-secondary mt-0.5 size-4 shrink-0"
                            titleAccess={t('invoices.payers.read-only')}
                        />
                        <p className="text-body2 text-fg-secondary">
                            <FormattedMessage
                                id="invoices.payers.totals.rows-locked-description"
                                values={{ count: invoice.sharedPaymentGroup?.invoiceCount ?? 1 }}
                            />
                        </p>
                    </div>
                ) : null}
                <InvoiceMatterTotals
                    invoiceTotals={invoiceTotals}
                    classifiers={classifiers}
                    isInViewMode={isInViewMode || isTotalsReadOnly || isLoading}
                    handleAddRow={(data) => {
                        void handleAddRow(data);
                    }}
                    handleAddAdminFee={(data) => {
                        void handleAddAdminFee(data);
                    }}
                    invoice={invoice}
                />
                {/* A share's amounts follow the group's, so its rounding cannot be adjusted on its own. */}
                {hasRoundingMatrixPermission && !hasPaymentShare ? (
                    <RoundingMatrixAccordion
                        invoice={invoice}
                        invoiceTotals={invoiceTotals}
                        isInViewMode={isInViewMode}
                    />
                ) : null}
                <div className={SECTION_CARD}>
                    <div className={SECTION_HEADER}>
                        <p className={cn(SECTION_TITLE, 'my-2.5 ml-2')}>
                            <FormattedMessage id={titleId} />
                        </p>
                    </div>
                    <div className="border-divider border-t">
                        {hasShareTotals ? (
                            <SharedPaymentTotalsCard invoice={invoice} invoiceTotals={invoiceTotals} />
                        ) : (
                            <InvoiceTotalsCard invoice={invoice} invoiceTotals={invoiceTotals} />
                        )}
                    </div>
                </div>
                <RecalculatingOverlay invoiceId={invoice.id} />
            </div>
        </FormProvider>
    );
};

export default Totals;
